import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitContactForm from "@sunSource/Handlers/ContactUsForm/SubmitContactForm";
import submitRequestForQuoteForm from "@sunSource/Handlers/Custom/RequestForQuoteHandler";
import React, { FC, useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    submitRequestForQuoteForm,
    loadCurrentCountries,
    submitContactForm,
    setFieldValue,
};

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    currentUserIsGuest: getCurrentUserIsGuest(state),
    userName: state.context.session.userName,
    currentLocation: getLocation(state),
    getERPData: getSession(state),
    cart: getCurrentCartState(state).value,
    userDetails: getBillToState(state, state.context.session.billToId).value,
    firstName: state.components.contactUsForm.fieldValues["firstName"],
    lastName: state.components.contactUsForm.fieldValues["lastName"],
    companyName: state.components.contactUsForm.fieldValues["companyName"],
    emailAddress: state.components.contactUsForm.fieldValues["emailAddress"],
    phoneNumber: state.components.contactUsForm.fieldValues["phoneNumber"],
    topic: state.components.contactUsForm.fieldValues["topic"],
    zipcodeName: state.components.contactUsForm.fieldValues["zipcodeName"],
    modelName: state.components.contactUsForm.fieldValues["modelName"],
    supplierName: state.components.contactUsForm.fieldValues["supplierName"],
    message: state.components.contactUsForm.fieldValues["message"],
    customerNumber: state.components.contactUsForm.fieldValues["customerNumber"],
    currentUrl: state.components.contactUsForm.fieldValues["currentUrl"],
    vendor: state.components.contactUsForm.fieldValues["vendor"],
    page: getCurrentPage(state),
});

interface OwnProps {
    successMessage: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

export interface RequestForQuoteFormStyles {
    submitErrorGridItem: GridItemProps;
    submitErrorTitle: TypographyProps;
    notesText: TypographyProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    container?: InjectableCss;
    headcontainer?: InjectableCss;
    formTextField?: TextFieldPresentationProps;
    messageTextArea?: TextAreaProps;
    formFieldGridItem?: GridItemProps;
    formFieldGridItemFullWidth?: GridItemProps;
    formFieldsContainer?: GridContainerProps;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    notes?: GridItemProps;
    userGridContainer?: GridContainerProps;
    stateSelect?: InjectableCss;
}

export const requestForQuoteFormStyles: RequestForQuoteFormStyles = {
    headcontainer: {
        css: css`
            box-sizing: border-box;
            width: 100%;
            background: rgba(225, 221, 226, 1);
            border: 1px solid #e1dde2;
            border-radius: 8px 8px 0px 0px;
        `,
    },

    container: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            padding: 15px;
            border-radius: 0px 0px 8px 8px;

            margin-bottom: 0px;
            justify-content: space-evenly;
            [class^="FormFieldStyle"] {
                order: 1;
                justify-content: center;
                display: flex;
            }
            [class^="FormInputWrapper"] {
                margin-top: 4px !important;
            }
            [class^="UploadWrapper"] {
                margin-bottom: 11px;
            }
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    formTextField: {
        css: css`
            border-radius: 8px !important;
            padding: 0px;
        `,
    },
    messageTextArea: {
        css: css`
            resize: none;
            :focus {
                border: 2px solid ${getColor("primary")} !important;
            }
        `,
    },
    formFieldsContainer: {
        gap: 0,
        css: css`
            > [class^="GridOffset-sc-"] {
                justify-content: space-between;
            }
        `,
    },

    formFieldGridItem: {
        width: 6,
        css: css`
            padding: 5px 0;
            max-width: 48%;
            input,
            select {
                border-radius: 0 !important;
            }
        `,
    },
    formFieldGridItemFullWidth: {
        width: 12,
        css: css`
            padding: 5px 0;
            input,
            select {
                border-radius: 0 !important;
            }
        `,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    submitGridItem: {
        width: 12,
        css: css`
            padding: 10px 0;
        `,
    },
    stateSelect: {
        css: css`
            border-radius: 8px !important;
            padding: 0px;
        `,
    },
    submitButton: {
        css: css`
            width: 85%;
            margin: 0 auto;
        `,
    },
    notes: {
        width: 12,
        css: css`
            margin-top: 15px !important;
        `,
    },
    notesText: {
        variant: "p",
        css: css`
            font-size: 14px;
            line-height: 20px;
        `,
    },
};

const styles = requestForQuoteFormStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

const RequestForQuoteForm = ({
    loadCurrentCountries,
    countries,
    submitContactForm,
    setFieldValue,
    successMessage,
    getERPData,
    currentLocation,
    userDetails,
    cart,
    product,
    page
}: Props) => {
    const firstNameRequiredFieldMessage = siteMessage("First Name is required");
    const lastNameRequiredFieldMessage = siteMessage("Last Name is required");
    const companyRequiredFieldMessage = siteMessage("Company is required");
    const emailRequiredFieldMessage = siteMessage("Email is Required");
    const stateRequiredFieldMessage = siteMessage("State is required");
    const zipCodeRequiredFieldMessage = siteMessage("Zip Code is required");

    const [userFirstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState(firstNameRequiredFieldMessage);
    const [userLastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState(lastNameRequiredFieldMessage);
    const [company, setCompany] = useState("");
    const [companyError, setCompanyError] = useState(companyRequiredFieldMessage);
    const [supplier, setSupplier] = useState<string | undefined>("");
    const [model, setModel] = useState<string | undefined>("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [zipCodeError, setZipCodeError] = useState(zipCodeRequiredFieldMessage);
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [stateError, setStateError] = useState(stateRequiredFieldMessage);
    const [additionalDetailsInquiry, setAdditionalDetailsInquiry] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const [allStates, setAllStates] = useState<any>();

    React.useEffect(() => {
        loadCurrentCountries();
    }, []);

    React.useEffect(() => {
        if (countries) {
            const getStates = countries?.find((option: any) => option.name === "United States");
            setAllStates(getStates?.states);
        }
    }, [countries]);

    React.useEffect(() => {
        if (currentLocation.pathname.includes("Cart") && cart?.cartLines?.length) {
            const rfqProducts = cart?.cartLines?.filter(item => item.quoteRequired);
            const suppliers = rfqProducts
                ?.map(item => item.brand?.name)
                .filter((item, pos, self) => self.indexOf(item) === pos)
                .join(", ");
            const model = rfqProducts?.map(item => item.erpNumber).join(", ");
            const vendorData = rfqProducts
                .map(item =>
                    item.properties.vendor
                        ? `${item.erpNumber}: ${item.properties.vendor}`
                        : `${item.erpNumber}: SunSource`,
                )
                .join(", ");
            setFieldValue({ key: "vendor", value: vendorData || "" });
            supplierChangeHandler(suppliers || "");
            modelChangeHandler(model || "");
            firstNameChangeHandler(userDetails?.firstName || "");
            lastNameChangeHandler(userDetails?.lastName || "");
            companyChangeHandler(userDetails?.companyName || "");
            emailChangeHandler(userDetails?.email || "");
            phoneHandler(userDetails?.phone || "");
            zipChangeHandler(userDetails?.postalCode || "");
            stateChangeHandler(userDetails?.state?.name || "");
        }
    }, [currentLocation, userDetails, cart]);

    React.useEffect(() => {
        if(page.type === "ProductDetailsPage" && product) {
            const vendor = product?.properties?.vendor
            ? `${product.productNumber}: ${product.properties.vendor}`
            : `${product.productNumber}: ${siteMessage("SunSource")}`

            setFieldValue({ key: "vendor", value: vendor ? [vendor].join(", ") as any : "" });
        }
    }, [page, product]);
    React.useEffect(() => {
        setFieldValue({ key: "customerNumber", value: getERPData.properties.billToErpNumber || "" });
        setFieldValue({ key: "currentUrl", value: currentLocation.pathname || "" });
    }, [currentLocation, getERPData]);

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };
    const validateLastName = (value: string) => {
        const errorMessage = !value ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validateZipCode = (name: string) => {
        const zipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
        const errorMessage = !name ? zipCodeRequiredFieldMessage : !zipRegex.test(name) ? "Enter Valid Zip Code" : "";
        setZipCodeError(errorMessage);
        return !errorMessage;
    };

    const validateCompany = (value: string) => {
        const errorMessage = !value ? companyRequiredFieldMessage : "";
        setCompanyError(errorMessage);
        return !errorMessage;
    };

    const addDashToPhone = (value: string) => {
        if (value.length > 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        }
        if (value.length > 3 && value.length <= 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}`;
        }
        return value;
    };

    const removeDashFromPhone = (value: string) => {
        return value.replace(/\D/g, "");
    };

    const phoneHandler = (value: string) => {
        const regex = /[0-9]|\./;
        if (!value) {
            setPhone("");
            setPhoneError("");
            setFieldValue({ key: "phoneNumber", value: "" });
            return;
        }
        value = removeDashFromPhone(value);
        if (!regex.test(value) || value.length !== 10) {
            setPhone(value);
            setPhoneError("Phone Number is not Valid");
        } else {
            setPhoneError("");
        }
        if (value) {
            const formattedPhone = addDashToPhone(value);
            formattedPhone !== undefined && setPhone(formattedPhone);
            setFieldValue({ key: "phoneNumber", value: formattedPhone });
        }
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : "Email is not valid";
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateState = (value: string) => {
        const errorMessage = !value ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            firstNameError ||
            lastNameError ||
            companyError ||
            emailError ||
            stateError ||
            zipCodeError ||
            phoneError
        );
    };

    const firstNameChangeHandler = (value: string) => {
        validateFirstName(value);
        setFieldValue({ key: "firstName", value });
        setFirstName(value);
    };
    const lastNameChangeHandler = (value: string) => {
        validateLastName(value);
        setFieldValue({ key: "lastName", value });
        setLastName(value);
    };
    const companyChangeHandler = (value: string) => {
        validateCompany(value);
        setFieldValue({ key: "companyName", value });
        setCompany(value);
    };
    const supplierChangeHandler = (value: string) => {
        setSupplier(value);
        setFieldValue({ key: "supplierName", value });
    };
    const modelChangeHandler = (value: string) => {
        setModel(value);
        setFieldValue({ key: "modelName", value });
    };
    const stateChangeHandler = (value: string) => {
        validateState(value);
        setFieldValue({ key: "topic", value });
        setState(value);
    };
    const zipChangeHandler = (value: string) => {
        validateZipCode(value);
        setZip(value);
        setFieldValue({ key: "zipcodeName", value });
    };
    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setFieldValue({ key: "emailAddress", value: email });
        setEmail(email);
    };
    const additionalDetailsInquiryChangeHandler = (value: string) => {
        setAdditionalDetailsInquiry(value);
        setFieldValue({ key: "message", value });
    };

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setCompany("");
        setSupplier("");
        setModel("");
        setState("");
        setZip("");
        setPhone("");
        setEmail("");
        setAdditionalDetailsInquiry("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setFirstNameError(firstNameRequiredFieldMessage);
        setLastNameError(lastNameRequiredFieldMessage);
        setCompanyError(companyRequiredFieldMessage);
        setEmailError(emailRequiredFieldMessage);
        setStateError(stateRequiredFieldMessage);
        setZipCodeError(zipCodeRequiredFieldMessage);
    };
    // eslint-disable-next-line require-await
    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);
        const emailRecipients = "";
        submitContactForm({
            emailRecipients,
            onSuccess: () => {
                setErrorMessage("");
                toasterContext.addToast({
                    body: translate(`${successMessage}`),
                    messageType: "success",
                    timeoutLength: 10000,
                });
                clearFormFields();

                function getDataLayer() {
                    return (window as any).dataLayer;
                }
                const dataLayer = getDataLayer();
                if (dataLayer) {
                    dataLayer.push({
                        event: `RFQ-form-submitted`,
                    });
                }
                setIsSubmitting(false);
            },
            onComplete() {
            },
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();
    return (
        <>
            <StyledWrapper {...styles.container} className="RFQ-container">
                <StyledForm onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.formFieldsContainer}>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("First Name")}
                                onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                onBlur={e => validateFirstName(e.currentTarget.value)}
                                value={userFirstName}
                                data-test-selector="rfq_firstname"
                                required
                                error={isSubmitted && firstNameError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Last Name")}
                                onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                value={userLastName}
                                data-test-selector="rfq_lastname"
                                required
                                error={isSubmitted && lastNameError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Email Address")}
                                onChange={e => emailChangeHandler(e.currentTarget.value)}
                                value={email}
                                data-test-selector="rfq_email"
                                required
                                error={isSubmitted && emailError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Phone Number")}
                                onChange={e => phoneHandler(e.currentTarget.value)}
                                value={phone}
                                data-test-selector="rfq_phone"
                                error={phoneError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItemFullWidth}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Company / Organization")}
                                onChange={e => companyChangeHandler(e.currentTarget.value)}
                                value={company}
                                data-test-selector="rfq_company"
                                required
                                error={isSubmitted && companyError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <Select
                                {...styles.stateSelect}
                                data-test-selector="rfq_state"
                                defaultValue=""
                                placeholder="Select State"
                                value={state}
                                required
                                error={isSubmitted && stateError}
                                onChange={e => stateChangeHandler(e.currentTarget.value)}
                            >
                                <option value="">Select State</option>
                                <option value="Canada">Canada</option>
                                <option value="Mexico">Mexico</option>
                                <option value="International">International</option>
                                {allStates?.map((state: any, index: number) => {
                                    return (
                                        <option value={state.name} key={index}>
                                            {state.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Zip Code")}
                                onChange={e => zipChangeHandler(e.currentTarget.value)}
                                value={zip}
                                required
                                data-test-selector="rfq_zip"
                                error={isSubmitted && zipCodeError}
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Supplier")}
                                onChange={e => supplierChangeHandler(e.currentTarget.value)}
                                value={supplier}
                                data-test-selector="rfq_supplier"
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                placeholder={translate("Model or Series")}
                                onChange={e => modelChangeHandler(e.currentTarget.value)}
                                value={model}
                                data-test-selector="rfq_modelorseries"
                            />
                        </GridItem>
                        <GridItem {...styles.formFieldGridItemFullWidth}>
                            <TextArea
                                {...styles.messageTextArea}
                                // type="text"
                                placeholder={translate(
                                    "Please share any additional information that may be helpful on your quote request.",
                                )}
                                onChange={e => additionalDetailsInquiryChangeHandler(e.currentTarget.value)}
                                value={additionalDetailsInquiry}
                                data-test-selector="rfq_additionaldetails"
                                rows={4}
                            />
                        </GridItem>
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                data-test-selector="createAccount_createButton"
                            >
                                {translate("Submit")}
                            </Button>
                        </GridItem>
                        {currentLocation.pathname.includes("Cart") && cart?.cartLines?.length ? (
                            <GridItem {...styles.notes}>
                                <Typography {...styles.notesText}>
                                    <strong>Note: </strong>
                                    {siteMessage("RFQ_InfoMessage")}
                                </Typography>
                            </GridItem>
                        ) : null}
                    </GridContainer>
                </StyledForm>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(RequestForQuoteForm));
